import {useState} from 'react';
import styled from 'styled-components';
import {Button, DropdownHandler, EMPHASIS, Icon} from '@forecast-it/design-system';
import {LabelledButton} from './LabelledButton';
import {useTicketPerms} from '../../auth/hooks/useTicketPerms';

interface Props {
	watchCount?: number | null;
	isWatching?: boolean;
	onWatch?: () => void;
	onUnwatch?: () => void;
	watchInFlight?: boolean;
	status: string;
	/**
	 * Retained for caller compatibility. Resolve does not gate on ticket
	 * ownership (it gates on the manage permission), so this is not read here.
	 */
	assigneeId?: string | null;
	onResolve: () => void;
	/** Any ticket write in flight. Disables Resolve so its rollback can't clobber another save. */
	saving?: boolean;
	/** A write setting status to resolved is in flight. Drives the "Resolving…" label only. */
	resolving?: boolean;
	/** When provided (the caller gates on the manage permission), the menu offers Delete. */
	onDelete?: () => void;
}

const Bar = styled.div`
	display: flex;
	flex: none;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
`;
const Toast = styled.div`
	position: fixed;
	bottom: 16px;
	right: 16px;
	z-index: 50;
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[300]};
	border-radius: ${p => p.theme.sys.size.borderradius['2']};
	background: ${p => p.theme.sys.color.background.neutral['4']};
	padding: ${p => p.theme.sys.spacing[300]} ${p => p.theme.sys.spacing[400]};
	${p => p.theme.sys.font?.subheading?.['2']}
	color: ${p => p.theme.sys.color.text.content.ondark};
`;

/**
 * The detail header's actions: watch, resolve, and a menu for everything that
 * should not compete with them.
 *
 * Resolve is the one primary action on the page, so it is the only high-emphasis
 * control. Watch is a toggle, and reads as one — tonal while following, quiet
 * otherwise. Copy link and delete live behind the overflow menu: an always-visible
 * delete next to resolve invites the wrong press.
 */
export function TicketActionBar({
	watchCount,
	isWatching,
	onWatch,
	onUnwatch,
	watchInFlight,
	status,
	onResolve,
	saving,
	resolving,
	onDelete,
}: Props) {
	const {canManage} = useTicketPerms();
	const [showCopiedToast, setShowCopiedToast] = useState(false);

	async function handleShare() {
		await navigator.clipboard.writeText(window.location.href);
		setShowCopiedToast(true);
		setTimeout(() => setShowCopiedToast(false), 2000);
	}

	const isAlreadyResolved = status === 'resolved' || status === 'closed';

	// Resolve is an updateTicket-backed write, so it gates on the same manage
	// permission the gateway enforces. There is no "assignee may resolve their
	// own" path — that would now come back FORBIDDEN.
	const canResolve = !isAlreadyResolved && canManage;

	const resolveTooltip = (() => {
		if (isAlreadyResolved) return 'This ticket is already resolved';
		if (!canResolve) return "You don't have permission to resolve this ticket.";
		return undefined;
	})();

	const watchLabel = watchInFlight
		? '…'
		: `${isWatching ? 'Following' : 'Watch'}${watchCount != null && watchCount > 0 ? ` · ${watchCount}` : ''}`;

	return (
		<Bar>
			<Button
				emphasis={isWatching ? EMPHASIS.MEDIUM : EMPHASIS.MONO}
				icon="watcher"
				disabled={watchInFlight}
				onPress={isWatching ? onUnwatch : onWatch}
				data-testid="watch-button"
			>
				{watchLabel}
			</Button>

			<Button
				emphasis={EMPHASIS.HIGH}
				icon="tick"
				disabled={!canResolve || saving}
				title={resolveTooltip}
				onPress={onResolve}
				data-testid="resolve-button"
			>
				{resolving ? 'Resolving…' : 'Resolve'}
			</Button>

			<DropdownHandler>
				<DropdownHandler.Trigger>
					<LabelledButton emphasis={EMPHASIS.LOW} icon="more" label="More actions" data-cy="ticket-actions" />
				</DropdownHandler.Trigger>
				<DropdownHandler.Content offset={4}>
					<DropdownHandler.Item onClick={handleShare}>
						<Icon icon="copyLink" size="s" />
						Copy link
					</DropdownHandler.Item>
					{onDelete && <DropdownHandler.Separator />}
					{onDelete && (
						<DropdownHandler.Item onClick={onDelete}>
							<Icon icon="trash" size="s" />
							Delete ticket
						</DropdownHandler.Item>
					)}
				</DropdownHandler.Content>
			</DropdownHandler>

			{showCopiedToast && <Toast role="alert">Link copied</Toast>}
		</Bar>
	);
}
